import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, CTABand, Reveal, TickList } from "@/components/ui-bits";
import { PORT_IMAGE, waLink, track, WHO_WE_SERVE } from "@/lib/site";
import { MessageCircle } from "lucide-react";

const FEATURES = [
  "Custom packaging",
  "Retail-ready packaging",
  "Bulk packaging",
  "Buyer-specific requirements",
  "Private-label enquiry support",
  "Product customization where feasible",
];

const FORMATS = [
  { name: "Retail Packs", note: "Buyer-branded retail formats, confirmed by feasibility." },
  { name: "Bulk Bags", note: "Large-volume packaging for re-packing and processing." },
  { name: "Food-Grade Packaging", note: "Packaging suitable for food products, per agreed specification." },
  { name: "Cartons", note: "Carton packaging configured to the buyer's requirement." },
  { name: "Custom Printed Packaging", note: "Buyer artwork and branding, subject to confirmation." },
];

export default function PrivateLabel() {
  return (
    <>
      <Seo
        title="Private Label & Custom Packaging — Indian Food Products | Orvella Overseas"
        description="Private label and custom packaging for Indian food products — retail-ready packs, bulk packaging and buyer-specific requirements for importers, distributors, retailers and food brands."
        path="/private-label"
        image={PORT_IMAGE}
      />

      <PageHero
        eyebrow="Private Label"
        title="Private Label & Custom Packaging"
        sub="Build your brand with Indian food products — packaging and labelling arranged around your requirement, where feasible."
      />

      <section className="section-pad">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="eyebrow">Who It's For</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">For Businesses Building Their Own Brands</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {WHO_WE_SERVE.map((w) => <span key={w.title} className="chip">{w.title}</span>)}
            </div>
            <h3 className="mt-8 font-bold text-navy">What we support</h3>
            <TickList className="mt-4" items={FEATURES} />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-xl border border-line">
              <img src={PORT_IMAGE} alt="Container port with cranes loading cargo for international shipment" loading="lazy" className="h-auto w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section id="packaging" className="section-pad bg-sand" data-testid="packaging-section">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">Packaging</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">Packaging Formats</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mist">
              Packaging options are confirmed based on the product and buyer requirement — formats below are discussed and
              agreed per order, not assumed.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FORMATS.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.04}>
                <div className="card card-hover h-full p-6" data-testid={`packaging-format-${f.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <h3 className="font-bold text-navy">{f.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{f.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x flex flex-col items-center text-center">
          <Reveal>
            <h2 className="max-w-2xl font-serif text-3xl font-semibold text-navy">Have a private label requirement?</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-mist">
              Share your product, target market and packaging idea — our export team will confirm feasibility and respond with options.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <a
                href={waLink("Hello Orvella Overseas, I would like to discuss a private label / custom packaging requirement for Indian food products.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="private-label-discuss-btn"
                onClick={() => track("whatsapp_click", { context: "private_label" })}
                className="btn-gold"
              >
                <MessageCircle size={16} /> Discuss Your Private Label Requirement
              </a>
              <Link to="/quote" className="btn-navy">Request a Quote</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
